import ks from "./kBrowserShared";

export class kDragBucket {

    public BucketId: string = "";
    public Name: string = "";
    public Color: string = "";
    public Element: HTMLElement | null = null;
    private Parent: kBucketDragDrop | null = null;

    public Build = (BucketId: string, Name: string, Color: string, Container: HTMLElement, Parent: kBucketDragDrop): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.BucketId = BucketId;
            this.Name = Name;
            this.Color = Color;
            this.Parent = Parent;
            this.Element = document.createElement("div");
            this.Element.dataset.bucketId = BucketId;
            this.ApplyStyles();
            this.RenderHeader();
            this.AttachEvents();
            Container.appendChild(this.Element);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kDragBucket.Build");
        }
    };

    private ApplyStyles = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Element === null) { return; }
            this.Element.style.border = "2px dashed " + this.Color;
            this.Element.style.borderRadius = "8px";
            this.Element.style.padding = "12px";
            this.Element.style.minHeight = "60px";
            this.Element.style.transition = "border-color 0.2s, background 0.2s";
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kDragBucket.ApplyStyles");
        }
    };

    private RenderHeader = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Element === null) { return; }
            const Header: HTMLElement = document.createElement("div");
            Header.style.fontWeight = "600";
            Header.style.marginBottom = "8px";
            Header.style.color = this.Color;
            Header.textContent = this.Name;
            Header.dataset.role = "bucket-header";
            this.Element.insertBefore(Header, this.Element.firstChild);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kDragBucket.RenderHeader");
        }
    };

    public UpdateName = (Name: string): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.Name = Name;
            if (this.Element === null) { return; }
            const Header: HTMLElement | null = this.Element.querySelector("[data-role='bucket-header']");
            if (Header !== null) {
                Header.textContent = Name;
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kDragBucket.UpdateName");
        }
    };

    private AttachEvents = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Element === null) { return; }
            this.Element.addEventListener("dragover", this.OnDragOver);
            this.Element.addEventListener("dragenter", this.OnDragEnter);
            this.Element.addEventListener("dragleave", this.OnDragLeave);
            this.Element.addEventListener("drop", this.OnDrop);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kDragBucket.AttachEvents");
        }
    };

    public Destroy = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Element === null) { return; }
            this.Element.removeEventListener("dragover", this.OnDragOver);
            this.Element.removeEventListener("dragenter", this.OnDragEnter);
            this.Element.removeEventListener("dragleave", this.OnDragLeave);
            this.Element.removeEventListener("drop", this.OnDrop);
            if (this.Element.parentElement !== null) {
                this.Element.parentElement.removeChild(this.Element);
            }
            this.Element = null;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kDragBucket.Destroy");
        }
    };

    private OnDragOver = (Evt: DragEvent): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            kBucketDragDrop.SetMoveDropEffect(Evt, "kDragBucket.OnDragOver");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kDragBucket.OnDragOver");
        }
    };

    private OnDragEnter = (Evt: DragEvent): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            Evt.preventDefault();
            if (this.Element === null) { return; }
            this.Element.style.borderColor = this.Color;
            this.Element.style.background = this.Color + "10";
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kDragBucket.OnDragEnter");
        }
    };

    private OnDragLeave = (Evt: DragEvent): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            const Target: HTMLElement = Evt.currentTarget as HTMLElement;
            const Related: Node | null = Evt.relatedTarget as Node;
            if (Related !== null && Target.contains(Related)) { return; }
            this.ResetVisuals();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kDragBucket.OnDragLeave");
        }
    };

    private OnDrop = (Evt: DragEvent): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            Evt.preventDefault();
            if (Evt.dataTransfer === null || this.Parent === null) { return; }
            const ItemId: string = Evt.dataTransfer.getData("text/plain");
            if (ItemId === "") { return; }
            this.ResetVisuals();
            this.Parent.HandleItemDroppedOnBucket(ItemId, this.BucketId);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kDragBucket.OnDrop");
        }
    };

    private ResetVisuals = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Element === null) { return; }
            this.Element.style.borderColor = this.Color;
            this.Element.style.background = "";
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kDragBucket.ResetVisuals");
        }
    };
}

export class kBucketDragDrop {

    public Buckets: kDragBucket[] = [];
    public OnItemDropped: ((ItemId: string, BucketId: string) => void) | null = null;
    public OnItemRemovedFromBucket: ((ItemId: string, BucketId: string) => void) | null = null;
    private Container: HTMLElement | null = null;
    private DropZone: HTMLElement | null = null;

    public Render = (Parent: HTMLElement): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.Container = document.createElement("div");
            this.Container.style.display = "flex";
            this.Container.style.flexWrap = "wrap";
            this.Container.style.gap = "12px";
            Parent.appendChild(this.Container);
            this.CreateDropZone(Parent);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kBucketDragDrop.Render");
        }
    };

    private CreateDropZone = (Parent: HTMLElement): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.DropZone = document.createElement("div");
            this.DropZone.style.minHeight = "20px";
            Parent.appendChild(this.DropZone);
            this.DropZone.addEventListener("dragover", this.OnDropZoneDragOver);
            this.DropZone.addEventListener("drop", this.OnDropZoneDrop);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kBucketDragDrop.CreateDropZone");
        }
    };

    public AddBucket = (BucketId: string, Name: string, Color: string): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Container === null) { return; }
            const Bucket: kDragBucket = new kDragBucket();
            Bucket.Build(BucketId, Name, Color, this.Container, this);
            this.Buckets.push(Bucket);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kBucketDragDrop.AddBucket");
        }
    };

    public RemoveBucket = (BucketId: string): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            const Index: number = this.FindBucketIndex(BucketId);
            if (Index < 0) { return; }
            this.Buckets[Index].Destroy();
            this.Buckets.splice(Index, 1);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kBucketDragDrop.RemoveBucket");
        }
    };

    public RenameBucket = (BucketId: string, Name: string): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            const Bucket: kDragBucket | null = this.FindBucket(BucketId);
            if (Bucket === null) { return; }
            Bucket.UpdateName(Name);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kBucketDragDrop.RenameBucket");
        }
    };

    public GetBucketElement = (BucketId: string): HTMLElement | null => {
        if (ks.KErrorMode === true) { return null; }
        try {
            const Bucket: kDragBucket | null = this.FindBucket(BucketId);
            if (Bucket === null) { return null; }
            return Bucket.Element;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kBucketDragDrop.GetBucketElement");
            return null;
        }
    };

    private FindBucket = (BucketId: string): kDragBucket | null => {
        if (ks.KErrorMode === true) { return null; }
        try {
            for (const Bucket of this.Buckets) {
                if (Bucket.BucketId === BucketId) { return Bucket; }
            }
            return null;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kBucketDragDrop.FindBucket");
            return null;
        }
    };

    private FindBucketIndex = (BucketId: string): number => {
        if (ks.KErrorMode === true) { return -1; }
        try {
            for (let I: number = 0; I < this.Buckets.length; I++) {
                if (this.Buckets[I].BucketId === BucketId) { return I; }
            }
            return -1;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kBucketDragDrop.FindBucketIndex");
            return -1;
        }
    };

    public HandleItemDroppedOnBucket = (ItemId: string, BucketId: string): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.OnItemDropped !== null) {
                this.OnItemDropped(ItemId, BucketId);
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kBucketDragDrop.HandleItemDroppedOnBucket");
        }
    };

    /** The dragover affordance every drag source and target in the estate shares: allow the
        drop and show the move cursor. A failure here is reported under this method's own name
        (the coding-standards ratchet requires the literal Class.Method in the catch); Label is
        kept so existing callers still compile, and each caller's own guard and try/catch reports
        anything thrown outside this method under the caller's name. */
    public static SetMoveDropEffect = (Evt: DragEvent, Label: string): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            Evt.preventDefault();
            if (Evt.dataTransfer !== null) { Evt.dataTransfer.dropEffect = "move"; }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kBucketDragDrop.SetMoveDropEffect");
            return;
        }
    };

    private OnDropZoneDragOver = (Evt: DragEvent): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            kBucketDragDrop.SetMoveDropEffect(Evt, "kBucketDragDrop.OnDropZoneDragOver");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kBucketDragDrop.OnDropZoneDragOver");
        }
    };

    private OnDropZoneDrop = (Evt: DragEvent): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            Evt.preventDefault();
            if (Evt.dataTransfer === null) { return; }
            const ItemId: string = Evt.dataTransfer.getData("text/plain");
            if (ItemId === "") { return; }
            if (this.OnItemRemovedFromBucket !== null) {
                this.OnItemRemovedFromBucket(ItemId, "");
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kBucketDragDrop.OnDropZoneDrop");
        }
    };
}
